import cn from 'classnames';
import React, { useCallback, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import getGlobalConditionsFromOtherSpreads from 'editor/src/store/design/selector/getGlobalConditionsFromOtherSpreads';
import { ConditionGroup, conditionGroupChildenKey } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import { useSelector } from 'editor/src/store/hooks';
import { PluginName } from 'editor/src/store/plugins/types';

import useTr from 'editor/src/util/useTr';

import PersonalizationCondition from 'editor/src/component/PersonalizationContent/PersonalizationElements/PersonalizationCondition';

import ConditionElements from './ConditionElements';

import styles from './index.module.scss';

interface Props {
  conditionGroup: ConditionGroup | undefined;
}

function ConditionGroupBuilder({ conditionGroup }: Props) {
  const { spreadIndex, aiPersonalizationOn, globalConditions } = useSelector((state) => {
    const spreadIndex = getCurrentSpreadIndex(state);
    return {
      spreadIndex,
      aiPersonalizationOn: state.design.globalPlugins.includes(PluginName.AIPersonalization),
      globalConditions: getGlobalConditionsFromOtherSpreads(state, spreadIndex),
    };
  }, shallowEqual);
  const tr = useTr();

  const newConditionRef = useRef<string>();

  const onConditionCreated = useCallback((conditionId: string, edit: () => void) => {
    if (newConditionRef.current === conditionId) {
      newConditionRef.current = undefined;
      edit();
    }
  }, []);

  const children = conditionGroup?.children[conditionGroupChildenKey];

  if (!children?.length && globalConditions.length === 0) {
    return null;
  }

  return (
    <div className={cn(styles.ConditionGroupBuilder, 'cy-condition-builder')}>
      {conditionGroup && !!children?.length && (
        <div className={styles.list}>
          <ConditionElements
            spreadIndex={spreadIndex}
            conditionGroup={conditionGroup}
            parentCondition={undefined}
            elements={children}
            isEditing={false}
            onConditionCreated={onConditionCreated}
            textOnly={aiPersonalizationOn}
          />
        </div>
      )}
      {globalConditions.length > 0 && (
        // Outside the reorderable list — its position belongs to the spread
        // that stores it — but fully editable from here, and captioned with
        // where its linked elements live.
        <div className={cn(styles.globalConditions, 'cy-condition-builder-global')}>
          {globalConditions.map(
            ({ spreadIndex: owningSpreadIndex, spreadName, conditionGroup: owningGroup, condition }) => (
              <div key={condition.id}>
                <div className={styles.globalConditionTitle}>
                  {tr('editor-condition-stored-on-page', 'Shown on every page — stored on {{name}}', {
                    name: spreadName || `#${owningSpreadIndex + 1}`,
                  })}
                </div>
                <PersonalizationCondition
                  spreadIndex={owningSpreadIndex}
                  conditionGroup={owningGroup}
                  condition={condition}
                  isBuilding
                  textOnly={aiPersonalizationOn}
                  optionsOnly
                />
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(ConditionGroupBuilder);
